import { Skeleton } from '@mui/material';
import { extract, findRequired, isNotNullOrUndefined, withDefault } from '@vy/utils';
import React from 'react';
import { useSubsidiaries } from '../../../hooks/requests/use-subsidiaries';
import { useEffectOnce } from '../../../hooks/use-effect-once';
import { BaseSelect } from '../../design/selects/BaseSelect';
import type { StyledTextField } from '../../design/StyledTextField';

type SubsidiarySelectProps = Omit<React.ComponentProps<typeof StyledTextField>, 'options'> & {
  textFieldProps?: React.InputHTMLAttributes<HTMLInputElement>;
  suffix?: string;
  suffixByFieldValue?: Record<string, string>;
  label?: string;
  primaryAsDefault?: boolean;
};

export const SubsidiarySelect = React.forwardRef<HTMLInputElement, SubsidiarySelectProps>(
  ({ primaryAsDefault = false, ...props }, ref) => {
    const { data, isLoading } = useSubsidiaries();

    const subsidiaries = withDefault(data?.subsidiaries, []);
    const subsidiariesIds = extract(subsidiaries, 'id');

    const primarySubsidiary = subsidiaries.find((subsidiary) => subsidiary.isPrimary);

    useEffectOnce(() => {
      if (primaryAsDefault && isNotNullOrUndefined(primarySubsidiary)) {
        props.onChange?.({ target: { value: primarySubsidiary.id } } as any);
      }
    });

    if (isLoading) {
      return <Skeleton variant="rectangular" sx={{ width: '100px', height: '10px', borderRadius: '12px' }} />;
    }

    return (
      <BaseSelect
        label="Subsidiary"
        {...props}
        ref={ref}
        defaultValue={primaryAsDefault ? primarySubsidiary?.id : undefined}
        values={subsidiariesIds}
        getLabelOption={(option) => findRequired(subsidiaries, (subsidiary) => subsidiary.id === option).name}
      />
    );
  },
);
